import { TranslateFn } from 'hooks/useTranslate';
import { getSocietyTotalShares } from 'modules/society/utils/getSocietyTotalShares';
import { Partner, PartnerCategory, Society, StockPlan } from 'types';
import {
  getCategoryFDPercent,
  getCategoryNDPercent,
  getCategoryPotentialFD,
  getDecimalScale,
  getSocietyValue,
} from 'utils/filters';

type MapPartnerCategoriesToTable = {
  partnerCategories: PartnerCategory[];
  currentPartners: Partner[];
  actualSociety: Society;
  plans: StockPlan[];
  isReadOnly: boolean;
  isAdmin: boolean;
  translate: TranslateFn;
};

const defaultCategoriesOrder: Record<string, number> = {
  DEFAULT_CATEGORY_CO_OWNERSHIP_DESCRIPTION: 1,
  DEFAULT_CATEGORY_USUFRUCT_DESCRIPTION: 2,
  DEFAULT_CATEGORY_OTHER_ENCUMBRANCES_DESCRIPTION: 3,
};

export const sortPartnerCategoriesByDefault = (
  a: PartnerCategory,
  b: PartnerCategory
): number => {
  if (a.isDefault && b.isDefault) {
    const orderA =
      defaultCategoriesOrder[a.name as keyof typeof defaultCategoriesOrder] ||
      999;
    const orderB =
      defaultCategoriesOrder[b.name as keyof typeof defaultCategoriesOrder] ||
      999;
    return orderA - orderB;
  }

  if (a.isDefault && !b.isDefault) return -1;
  if (!a.isDefault && b.isDefault) return 1;

  const countA = a.partnersCount?.total || 0;
  const countB = b.partnersCount?.total || 0;
  return countB - countA;
};

export const mapPartnerCategoriesToTable = ({
  partnerCategories,
  currentPartners,
  actualSociety,
  isReadOnly,
  isAdmin,
  plans,
  translate,
}: MapPartnerCategoriesToTable) => {
  const societyValue = getSocietyValue(actualSociety);
  const decimalScale = societyValue ? getDecimalScale(societyValue.value) : 2;
  const societySharesCount = getSocietyTotalShares(actualSociety);

  return partnerCategories
    ?.sort((a, b) => sortPartnerCategoriesByDefault(a, b))
    ?.map((partnerCategory, index) => ({
      index: index + 1,
      partnerCategory,
      societyId: actualSociety._id,
      categoryId: partnerCategory._id,
      decimals: decimalScale,
      name: partnerCategory?.isDefault
        ? translate(partnerCategory.name)
        : partnerCategory.name,
      partnersCount: partnerCategory?.partnersCount?.total || 0,
      NDPercent: getCategoryNDPercent(
        partnerCategory,
        currentPartners,
        actualSociety?.sharesCount?.actual
      ),
      FDPercent: getCategoryFDPercent(
        partnerCategory,
        currentPartners,
        societySharesCount
      ),
      FDPotential: getCategoryPotentialFD(
        partnerCategory,
        currentPartners,
        societySharesCount,
        plans
      ),
      isAdmin,
      isReadOnly,
      isDefault: partnerCategory.isDefault,
    }));
};
